// frontend/src/components/EnhancedInsuranceIcons/HomeInsuranceIcon.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './EnhancedInsuranceIcons.module.css';

const HomeInsuranceIcon = ({ size = 120 }) => {
  const houseRef = useRef(null);
  
  useEffect(() => {
    if (!houseRef.current) return;
    
    const roof = houseRef.current.querySelector('#roof');
    const chimney = houseRef.current.querySelector('#chimney');
    const walls = houseRef.current.querySelector('#walls');
    const door = houseRef.current.querySelector('#door');
    const leftWindow = houseRef.current.querySelector('#left-window');
    const rightWindow = houseRef.current.querySelector('#right-window');
    const foundation = houseRef.current.querySelector('#foundation');
    
    // Hide all elements initially
    gsap.set([roof, chimney, walls, door, leftWindow, rightWindow, foundation], { 
      opacity: 0,
      y: -20
    });
    
    // Create animation timeline
    const tl = gsap.timeline({ defaults: { ease: "back.out(1.7)" } });
    
    // Animate each part dropping in with slight bounce effect - from roof to foundation
    tl.to(roof, { opacity: 1, y: 0, duration: 0.6, delay: 0.2 })
      .to(chimney, { opacity: 1, y: 0, duration: 0.5 }, "-=0.3")
      .to(walls, { opacity: 1, y: 0, duration: 0.5 }, "-=0.2")
      .to([leftWindow, rightWindow], { opacity: 0.8, y: 0, duration: 0.4, stagger: 0.1 }, "-=0.3")
      .to(door, { opacity: 1, y: 0, duration: 0.4 }, "-=0.3")
      .to(foundation, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2");
    
    // Add subtle continuous animation for a "hovering" effect
    gsap.to([roof, chimney], {
      y: "-=2",
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    gsap.to([walls, leftWindow, rightWindow, door], {
      y: "-=1.5",
      duration: 2,
      delay: 0.2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    return () => {
      // Clean up animations when component unmounts
      tl.kill();
      gsap.killTweensOf([roof, chimney, walls, door, leftWindow, rightWindow, foundation]);
    };
  }, []);
  
  return (
    <div className={styles.iconContainer} ref={houseRef} style={{ width: size, height: size }}>
      <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        {/* Triangular orange roof matching reference image */}
        <polygon 
          id="roof" 
          points="100,50 40,110 160,110" 
          fill="#F17B2C" 
          filter="url(#shadow)" 
        />
        
        {/* Chimney */}
        <rect 
          id="chimney" 
          x="140" 
          y="70" 
          width="15" 
          height="40" 
          fill="#9EA2AD" 
          filter="url(#shadow)" 
        />
        
        {/* White house walls */}
        <rect 
          id="walls" 
          x="60" 
          y="110" 
          width="80" 
          height="50" 
          fill="#FFFFFF" 
          filter="url(#shadow)" 
        />
        
        {/* Foundation/base */}
        <rect 
          id="foundation" 
          x="55" 
          y="160" 
          width="90" 
          height="5" 
          fill="#C8CAD0" 
          filter="url(#shadow)" 
        />
        
        {/* Windows - light blue squares */}
        <rect 
          id="left-window" 
          x="70" 
          y="120" 
          width="15" 
          height="15" 
          rx="2" 
          fill="#A0C4FF" 
          opacity="0.8" 
        />
        
        <rect 
          id="right-window" 
          x="115" 
          y="120" 
          width="15" 
          height="15" 
          rx="2" 
          fill="#A0C4FF" 
          opacity="0.8" 
        />
        
        {/* Door - dark gray */}
        <rect 
          id="door" 
          x="90" 
          y="135" 
          width="20" 
          height="25" 
          fill="#5E636E" 
        />
        
        {/* Filters for shadow effects */}
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="3" floodOpacity="0.3" />
          </filter>
        </defs>
      </svg>
    </div>
  );
};

export default HomeInsuranceIcon;